import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";

// Staff workspace. Tabs follow the capabilities the backend reports, so a
// person is never shown a section that would refuse them when clicked; the
// backend refuses regardless, and this only keeps the UI honest about it.
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const peso = (centavos) =>
  `₱${((centavos || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const units = (hundredths) => (Number(hundredths || 0) / 100).toFixed(2);
const hoursFrom = (minutes) => `${Math.floor((minutes || 0) / 60)}h ${(minutes || 0) % 60}m`;
const titleise = (value) => String(value || "").replaceAll("_", " ").toLowerCase();

const DOCUMENT_KINDS = [
  "GOVERNMENT_ID", "DRIVERS_LICENCE", "NBI_CLEARANCE", "EMPLOYMENT_CONTRACT",
  "TAX_DOCUMENT", "TRAINING_CERTIFICATE", "MEDICAL_CERTIFICATE", "LEAVE_SUPPORTING", "OTHER",
];

function Card({ title, children, action, testId }) {
  return <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testId}>
    <div className="mb-4 flex items-center justify-between gap-3"><h2 className="font-semibold text-slate-900">{title}</h2>{action}</div>
    {children}
  </section>;
}

function Empty({ children }) {
  return <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">{children}</div>;
}

function Stat({ label, value }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
  </div>;
}

function Table({ head, children }) {
  return <div className="overflow-x-auto"><table className="min-w-full text-left text-sm">
    <thead><tr className="border-b text-xs uppercase tracking-wide text-slate-500">
      {head.map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
    </tr></thead>
    <tbody>{children}</tbody>
  </table></div>;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function StaffManagement() {
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [selected, setSelected] = useState("");

  const caps = useMemo(() => data?.capabilities || {}, [data]);
  const employees = useMemo(() => data?.employees || [], [data]);
  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selected) || null, [employees, selected]);

  const tabs = useMemo(() => {
    const list = ["Overview", "My records"];
    if (caps.leave_management) list.push("People", "Calendar", "Leave", "Overtime");
    if (caps.attendance_management) list.push("Attendance");
    if (caps.documents) list.push("Documents");
    if (caps.compensation) list.push("Compensation");
    if (caps.payroll) list.push("Payroll");
    if (caps.leave_management) list.push("Reports");
    return list;
  }, [caps]);

  useEffect(() => { if (!tabs.includes(tab)) setTab("Overview"); }, [tabs, tab]);

  const load = useCallback(async () => {
    try {
      const [overview, mine] = await Promise.all([
        api.get("/staff-management/overview"),
        api.get("/staff-management/me"),
      ]);
      setData(overview.data);
      setMe(mine.data);
      setSelected((current) => current || overview.data.employees?.[0]?.id || "");
    } catch (err) { setError(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (work, message) => {
    setNotice(""); setError("");
    try { const result = await work(); if (message) setNotice(message); await load(); return result; }
    catch (err) { setError(formatApiError(err)); return null; }
  };

  if (!data) {
    return <div className="rounded-xl border bg-white p-6 text-slate-600" data-testid="staff-management-loading">
      {error || "Loading staff workspace..."}</div>;
  }

  return <div className="space-y-6" data-testid="staff-management-page">
    <header>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Staff</p>
      <h1 className="mt-1 text-2xl font-bold text-slate-950">Staff administration</h1>
      <p className="mt-1 max-w-3xl text-sm text-slate-600">
        Schedules, attendance, leave, documents, compensation and payroll for the people who run
        this business — each section visible only to the people allowed to use it.
      </p>
    </header>

    {notice && <div role="status" className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="staff-notice">{notice}</div>}
    {error && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900" data-testid="staff-error">{error}</div>}

    <nav className="flex gap-2 overflow-x-auto border-b border-slate-200 pb-2" aria-label="Staff administration sections">
      {tabs.map((item) => <button key={item} type="button" onClick={() => setTab(item)}
        data-testid={`staff-tab-${item.toLowerCase().replaceAll(" ", "-")}`}
        aria-current={tab === item ? "page" : undefined}
        className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium ${tab === item ? "bg-emerald-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{item}</button>)}
    </nav>

    {tab === "Overview" && <OverviewTab data={data} caps={caps} />}
    {tab === "My records" && <MyRecordsTab me={me} run={run} />}
    {tab === "People" && <PeopleTab employees={employees} selected={selected} setSelected={setSelected}
                                    selectedEmployee={selectedEmployee} caps={caps} run={run} />}
    {tab === "Calendar" && <CalendarTab />}
    {tab === "Leave" && <LeaveTab caps={caps} run={run} employees={employees} />}
    {tab === "Attendance" && <AttendanceTab employees={employees} caps={caps} run={run} />}
    {tab === "Overtime" && <OvertimeTab employees={employees} run={run} />}
    {tab === "Documents" && <DocumentsTab employees={employees} caps={caps} run={run} />}
    {tab === "Compensation" && <CompensationTab employees={employees} caps={caps} run={run} />}
    {tab === "Payroll" && <PayrollTab caps={caps} run={run} />}
    {tab === "Reports" && <ReportsTab caps={caps} />}
  </div>;
}

// ---------------------------------------------------------------- Overview --
function OverviewTab({ data, caps }) {
  const [queues, setQueues] = useState(null);
  useEffect(() => {
    if (!caps.leave_management) return;
    api.get("/staff-management/workspace/queues").then((r) => setQueues(r.data)).catch(() => setQueues(null));
  }, [caps.leave_management]);

  return <div className="space-y-5" data-testid="staff-overview">
    <div className="grid gap-3 sm:grid-cols-4">
      <Stat label="Employees" value={data.counts.employees} />
      <Stat label="Pending leave" value={data.counts.pending_leave} />
      <Stat label="Pending overtime" value={data.counts.pending_overtime} />
      <Stat label="Payroll periods" value={data.counts.payroll_periods} />
    </div>

    {queues && <Card title="Waiting for a decision" testId="staff-queues">
      <div className="grid gap-3 sm:grid-cols-5">
        <Stat label="Leave" value={queues.counts.leave} />
        <Stat label="Overtime" value={queues.counts.overtime} />
        <Stat label="Payroll to approve" value={queues.counts.payroll_awaiting_approval} />
        <Stat label="Payslips to issue" value={queues.counts.payroll_awaiting_payslips} />
        <Stat label="Documents expiring" value={queues.counts.documents_expiring} />
      </div>
      {queues.private_documents_expiring_count > 0 && <p className="mt-3 text-xs text-slate-500">
        {queues.private_documents_expiring_count} expiring document(s) are medical or leave
        attachments and are not listed here.
      </p>}
      {queues.documents_expiring?.length > 0 && <div className="mt-4">
        <Table head={["Employee", "Document", "Expires", "State"]}>
          {queues.documents_expiring.map((d) => <tr key={d.id} className="border-b last:border-0">
            <td className="py-2 pr-4">{d.employee_id.slice(0, 8)}</td>
            <td className="py-2 pr-4 capitalize">{titleise(d.kind)}</td>
            <td className={`py-2 pr-4 ${d.expired ? "font-semibold text-rose-700" : ""}`}>{d.expiry_date}{d.expired ? " (expired)" : ""}</td>
            <td className="py-2 pr-4 capitalize">{titleise(d.verification?.state)}</td>
          </tr>)}
        </Table>
      </div>}
    </Card>}

    <Card title="People and employment relationships">
      <Table head={["Name", "Title", "Relationship", "Capability"]}>
        {data.employees.map((employee) => <tr key={employee.id} className="border-b last:border-0">
          <td className="py-3 pr-4 font-medium text-slate-900">{employee.name || "Unnamed staff"}</td>
          <td className="py-3 pr-4 text-slate-600">{employee.title || "Not set"}</td>
          <td className="py-3 pr-4 capitalize text-slate-600">{titleise(employee.relationship)}</td>
          <td className="py-3 pr-4 text-slate-600">{employee.is_driver ? "Company driver" : "Staff"}</td>
        </tr>)}
      </Table>
    </Card>
  </div>;
}

// ------------------------------------------------------------- My records --
function MyRecordsTab({ me, run }) {
  const [attendance, setAttendance] = useState(null);
  const [payslips, setPayslips] = useState(null);
  const [documents, setDocuments] = useState([]);
  const myId = me?.employee?.id;

  const reload = useCallback(async () => {
    if (!myId) return;
    const [days, slips, docs] = await Promise.all([
      api.get(`/staff-management/attendance?start=${daysAgo(13)}&end=${today()}`).catch(() => null),
      api.get("/staff-management/me/payslips").catch(() => null),
      api.get(`/staff-management/employees/${myId}/documents`).catch(() => null),
    ]);
    setAttendance(days?.data || null);
    setPayslips(slips?.data || null);
    setDocuments(docs?.data?.items || []);
  }, [myId]);
  useEffect(() => { reload(); }, [reload]);

  if (!me) return <Empty>Your staff record is not available.</Empty>;
  const balances = me.leave_balance?.by_type || [];

  return <div className="space-y-5" data-testid="staff-my-records">
    <Card title="My leave balances">
      {balances.length ? <Table head={["Type", "Balance (days)", "Accrued", "Used"]}>
        {balances.map((row) => <tr key={row.leave_type_id} className="border-b last:border-0">
          <td className="py-2 pr-4">{row.name || row.code}</td>
          <td className="py-2 pr-4 font-medium">{units(row.balance_units)}</td>
          <td className="py-2 pr-4 text-slate-600">{units(row.accrued_units)}</td>
          <td className="py-2 pr-4 text-slate-600">{units(row.used_units)}</td>
        </tr>)}
      </Table> : <Empty>No leave balance has been recorded yet.</Empty>}
    </Card>

    <Card title="My attendance (last 14 days)" testId="staff-my-attendance">
      {attendance?.items?.length ? <>
        <p className="mb-3 text-sm text-slate-600">
          Worked {hoursFrom(attendance.summary.worked_minutes)} across {attendance.summary.present_days} day(s).
          {attendance.summary.late_days ? ` Late on ${attendance.summary.late_days} day(s).` : ""}
        </p>
        <Table head={["Date", "Status", "Expected", "Worked", "Late", "Corrected"]}>
          {attendance.items.map((day) => <tr key={day.date} className="border-b last:border-0">
            <td className="py-2 pr-4">{day.date}</td>
            <td className="py-2 pr-4 capitalize">{titleise(day.status)}</td>
            <td className="py-2 pr-4 text-slate-600">{day.expected_minutes ? hoursFrom(day.expected_minutes) : "—"}</td>
            <td className="py-2 pr-4">{day.worked_minutes ? hoursFrom(day.worked_minutes) : "—"}</td>
            <td className="py-2 pr-4">{day.late_minutes ? `${day.late_minutes}m` : "—"}</td>
            <td className="py-2 pr-4">{day.corrected ? "Yes" : "—"}</td>
          </tr>)}
        </Table>
      </> : <Empty>No attendance records for the last 14 days.</Empty>}
    </Card>

    <Card title="My payslips" testId="staff-my-payslips">
      {payslips?.items?.length ? <Table head={["Number", "Period", "Gross", "Net", "Issued"]}>
        {payslips.items.map((slip) => <tr key={slip.id} className="border-b last:border-0">
          <td className="py-2 pr-4 font-medium">{slip.payslip_number}</td>
          <td className="py-2 pr-4">{slip.snapshot?.period?.name}</td>
          <td className="py-2 pr-4">{peso(slip.gross_pay_centavos)}</td>
          <td className="py-2 pr-4 font-medium">{peso(slip.net_pay_centavos)}</td>
          <td className="py-2 pr-4 text-slate-600">{String(slip.issued_at).slice(0, 10)}</td>
        </tr>)}
      </Table> : <Empty>No payslip has been issued to you yet.</Empty>}
      {payslips?.pending?.length > 0 && <p className="mt-3 text-xs text-slate-500" data-testid="staff-payslip-pending">
        {payslips.pending.length} payroll period(s) are still being prepared. Those figures are not final
        and no payslip has been issued for them.
      </p>}
    </Card>

    <DocumentsCard title="My documents" employeeId={myId} documents={documents}
                   onChanged={reload} run={run} canVerify={false} />
  </div>;
}

// ----------------------------------------------------------------- People --
function PeopleTab({ employees, selected, setSelected, selectedEmployee, caps, run }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    if (!selected || !caps.leave_private) { setLedger(null); return; }
    api.get(`/staff-management/employees/${selected}/leave-ledger`)
      .then((r) => setLedger(r.data)).catch(() => setLedger(null));
  }, [selected, caps.leave_private]);

  return <div className="space-y-5" data-testid="staff-people">
    <Card title="Employee">
      <label className="block text-sm text-slate-700">Person
        <select className="mt-1 w-full rounded-lg border p-2" value={selected}
                data-testid="staff-employee-select"
                onChange={(e) => setSelected(e.target.value)}>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.id}</option>)}
        </select>
      </label>
      <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
        {selectedEmployee?.is_driver
          ? "This person is also a company driver. Approved leave becomes a driver availability block, and any assignment it affects is flagged for replacement rather than silently dropped."
          : "This person is not marked as a company driver."}
      </div>
    </Card>

    <ScheduleCard employeeId={selected} canEdit={caps.owner} run={run} />

    {caps.leave_private && <Card title="Leave ledger" testId="staff-leave-ledger">
      {ledger?.items?.length ? <Table head={["Date", "Type", "Units", "Reason"]}>
        {ledger.items.map((row) => <tr key={row.id} className="border-b last:border-0">
          <td className="py-2 pr-4">{String(row.created_at).slice(0, 10)}</td>
          <td className="py-2 pr-4 capitalize">{titleise(row.transaction_type)}</td>
          <td className={`py-2 pr-4 font-medium ${row.amount_units < 0 ? "text-rose-700" : "text-emerald-700"}`}>{units(row.amount_units)}</td>
          <td className="py-2 pr-4 text-slate-600">{row.reason}</td>
        </tr>)}
      </Table> : <Empty>No ledger entries for this person yet.</Empty>}
    </Card>}
  </div>;
}

function ScheduleCard({ employeeId, canEdit, run }) {
  const [schedule, setSchedule] = useState(null);
  const [grace, setGrace] = useState(0);
  useEffect(() => {
    if (!employeeId) return;
    api.get(`/staff-management/employees/${employeeId}/schedule`)
      .then((r) => { setSchedule(r.data.schedule); setGrace(r.data.schedule?.grace_minutes || 0); })
      .catch(() => setSchedule(null));
  }, [employeeId]);

  const saveGrace = () => run(
    () => api.put(`/staff-management/employees/${employeeId}/schedule`,
                  { ...(schedule || {}), grace_minutes: Number(grace) || 0 }),
    "Schedule updated.");

  return <Card title="Working schedule" testId="staff-schedule">
    {schedule ? <div className="space-y-2 text-sm">
      <p className="text-slate-600">Rest days: {(schedule.rest_days || []).join(", ") || "None recorded"}</p>
      <Table head={["Day", "Start", "End", "Unpaid break"]}>
        {Object.entries(schedule.weekly || {}).map(([day, entry]) => <tr key={day} className="border-b last:border-0">
          <td className="py-2 pr-4 capitalize">{day}</td>
          <td className="py-2 pr-4">{entry?.start || "—"}</td>
          <td className="py-2 pr-4">{entry?.end || "—"}</td>
          <td className="py-2 pr-4">{entry?.break_minutes ? `${entry.break_minutes}m` : "—"}</td>
        </tr>)}
      </Table>
    </div> : <Empty>No schedule has been set for this person.</Empty>}
    {canEdit && <div className="mt-4 flex flex-wrap items-end gap-3">
      <label className="text-sm">Grace period (minutes)
        <input type="number" min="0" max="480" className="mt-1 w-32 rounded-lg border p-2"
               data-testid="staff-grace-input" value={grace}
               onChange={(e) => setGrace(e.target.value)} />
      </label>
      <button type="button" onClick={saveGrace}
              className="rounded-lg border border-emerald-700 px-3 py-2 text-sm font-medium text-emerald-800">Save</button>
      <p className="text-xs text-slate-500">Zero means every minute after the scheduled start counts as late.</p>
    </div>}
  </Card>;
}

// ---------------------------------------------------------------- Calendar --
function CalendarTab() {
  const [range, setRange] = useState({ start: today(), end: daysAgo(-13) });
  const [calendar, setCalendar] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await api.get(`/staff-management/workspace/calendar?start=${range.start}&end=${range.end}`);
      setCalendar(r.data);
    } catch (err) { setError(formatApiError(err)); setCalendar(null); }
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const tone = {
    present: "bg-emerald-100 text-emerald-900", absent: "bg-rose-100 text-rose-900",
    on_leave: "bg-amber-100 text-amber-900", rest_day: "bg-slate-100 text-slate-500",
    closure: "bg-slate-100 text-slate-500", not_scheduled: "bg-white text-slate-400",
  };

  return <div className="space-y-5" data-testid="staff-calendar">
    <Card title="Staff calendar" action={<div className="flex gap-2">
      <input type="date" aria-label="Calendar start" className="rounded-lg border p-1.5 text-sm"
             value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
      <input type="date" aria-label="Calendar end" className="rounded-lg border p-1.5 text-sm"
             value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
    </div>}>
      <p className="mb-3 text-sm text-slate-600">
        Who is available, and when. Why somebody is away is never shown here.
      </p>
      {error && <div role="alert" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{error}</div>}
      {calendar?.items?.length ? <div className="overflow-x-auto"><table className="min-w-full text-left text-xs">
        <thead><tr className="border-b">
          <th className="py-2 pr-3 text-slate-500">Person</th>
          {(calendar.items[0].days || []).map((d) => <th key={d.date} className="py-2 pr-2 font-normal text-slate-500">{d.date.slice(5)}</th>)}
        </tr></thead>
        <tbody>{calendar.items.map((row) => <tr key={row.employee_id} className="border-b last:border-0">
          <td className="py-2 pr-3 font-medium text-slate-900">{row.name || row.employee_id.slice(0, 8)}</td>
          {row.days.map((d) => <td key={d.date} className="py-1 pr-2">
            <span className={`inline-block rounded px-1.5 py-0.5 ${tone[d.status] || "bg-white"}`}
                  title={`${d.date}: ${titleise(d.status)}`}>{titleise(d.status).slice(0, 3)}</span>
          </td>)}
        </tr>)}</tbody>
      </table></div> : <Empty>Nothing to show for this range.</Empty>}
    </Card>
  </div>;
}

// ------------------------------------------------------------------- Leave --
function LeaveTab({ caps, run, employees }) {
  const [types, setTypes] = useState([]);
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [form, setForm] = useState({ leave_type_id: "", start_date: today(), end_date: today(), unit: "day", reason: "" });
  const nameFor = (id) => employees.find((e) => e.id === id)?.name || String(id).slice(0, 8);

  const reload = useCallback(async () => {
    const [t, own, pending] = await Promise.all([
      api.get("/staff-management/leave-types").catch(() => null),
      api.get("/staff-management/leave").catch(() => null),
      caps.leave_management ? api.get("/staff-management/workspace/queues").catch(() => null) : null,
    ]);
    setTypes(t?.data?.items || []);
    setMine(own?.data?.items || []);
    setQueue(pending?.data?.leave || []);
  }, [caps.leave_management]);
  useEffect(() => { reload(); }, [reload]);

  const submit = async (event) => {
    event.preventDefault();
    const ok = await run(() => api.post("/staff-management/leave", form), "Leave request submitted.");
    if (ok) { setForm({ ...form, reason: "" }); await reload(); }
  };
  const decide = async (id, status) => {
    await run(() => api.post(`/staff-management/leave/${id}/decision`, { status }), `Leave ${status}.`);
    await reload();
  };

  return <div className="space-y-5" data-testid="staff-leave">
    {caps.leave_management && <Card title="Leave waiting for a decision" testId="staff-leave-queue">
      {queue.length ? <Table head={["Person", "Type", "Dates", "Days", "Decision"]}>
        {queue.map((row) => <tr key={row.id} className="border-b last:border-0">
          <td className="py-2 pr-4">{nameFor(row.employee_id)}</td>
          <td className="py-2 pr-4">{row.leave_type_snapshot?.name || "Leave"}</td>
          <td className="py-2 pr-4">{row.start_date} → {row.end_date}</td>
          <td className="py-2 pr-4">{units(row.amount_units)}</td>
          <td className="py-2 pr-4">
            <button type="button" data-testid={`staff-leave-approve-${row.id}`}
                    onClick={() => decide(row.id, "approved")}
                    className="mr-2 rounded-lg bg-emerald-800 px-3 py-1.5 text-xs font-semibold text-white">Approve</button>
            <button type="button" onClick={() => decide(row.id, "rejected")}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700">Reject</button>
          </td>
        </tr>)}
      </Table> : <Empty>No leave is waiting for a decision.</Empty>}
    </Card>}

    <div className="grid gap-5 lg:grid-cols-2">
      <Card title="Request leave">
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-sm">Leave type
            <select required className="mt-1 w-full rounded-lg border p-2" value={form.leave_type_id}
                    data-testid="staff-leave-type"
                    onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}>
              <option value="">Choose a type</option>
              {types.map((type) => <option key={type.id} value={type.id}>{type.name}{type.paid ? " (paid)" : " (unpaid)"}</option>)}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">Start<input required type="date" className="mt-1 w-full rounded-lg border p-2"
              value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
            <label className="block text-sm">End<input required type="date" className="mt-1 w-full rounded-lg border p-2"
              value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
          </div>
          <label className="block text-sm">Unit
            <select className="mt-1 w-full rounded-lg border p-2" value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}>
              <option value="day">Full day</option><option value="half_day">Half day</option><option value="hour">Hourly</option>
            </select>
          </label>
          <label className="block text-sm">Reason
            <textarea className="mt-1 w-full rounded-lg border p-2" rows="3" value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </label>
          <p className="text-xs text-slate-500">Your reason is visible only to you and to leave management.</p>
          <button className="rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white" data-testid="staff-leave-submit">Submit request</button>
        </form>
      </Card>

      <Card title="My leave">
        {mine.length ? <div className="space-y-2">{mine.map((item) => <div key={item.id} className="rounded-lg border p-3 text-sm">
          <div className="flex justify-between"><b>{item.leave_type_snapshot?.name || "Leave"}</b><span className="capitalize">{item.status}</span></div>
          <p className="text-slate-600">{item.start_date} to {item.end_date} · {units(item.amount_units)} day(s)</p>
        </div>)}</div> : <Empty>No leave requests yet.</Empty>}
      </Card>
    </div>
  </div>;
}

// -------------------------------------------------------------- Attendance --
function AttendanceTab({ employees, caps, run }) {
  const [employeeId, setEmployeeId] = useState(employees[0]?.id || "");
  const [range, setRange] = useState({ start: daysAgo(13), end: today() });
  const [days, setDays] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [correction, setCorrection] = useState({ work_date: today(), clock_in_at: "", clock_out_at: "", status_override: "", reason: "" });

  const reload = useCallback(async () => {
    if (!employeeId) return;
    const [d, e] = await Promise.all([
      api.get(`/staff-management/attendance?employee_id=${employeeId}&start=${range.start}&end=${range.end}`).catch(() => null),
      api.get("/staff-management/workspace/exceptions").catch(() => null),
    ]);
    setDays(d?.data || null);
    setExceptions(e?.data || null);
  }, [employeeId, range.start, range.end]);
  useEffect(() => { reload(); }, [reload]);

  const submitCorrection = async (event) => {
    event.preventDefault();
    const body = { work_date: correction.work_date, reason: correction.reason };
    if (correction.clock_in_at) body.clock_in_at = `${correction.work_date}T${correction.clock_in_at}:00`;
    if (correction.clock_out_at) body.clock_out_at = `${correction.work_date}T${correction.clock_out_at}:00`;
    if (correction.status_override) body.status_override = correction.status_override;
    const ok = await run(() => api.post(`/staff-management/employees/${employeeId}/attendance/corrections`, body),
                         "Attendance correction recorded. The employee has been notified.");
    if (ok) { setCorrection({ ...correction, reason: "", clock_in_at: "", clock_out_at: "", status_override: "" }); await reload(); }
  };

  return <div className="space-y-5" data-testid="staff-attendance">
    <Card title="Attendance" action={<div className="flex flex-wrap gap-2">
      <select className="rounded-lg border p-1.5 text-sm" value={employeeId} data-testid="staff-attendance-employee"
              aria-label="Attendance employee" onChange={(e) => setEmployeeId(e.target.value)}>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.id}</option>)}
      </select>
      <input type="date" aria-label="Attendance start" className="rounded-lg border p-1.5 text-sm"
             value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
      <input type="date" aria-label="Attendance end" className="rounded-lg border p-1.5 text-sm"
             value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
    </div>}>
      {days?.summary && <p className="mb-3 text-sm text-slate-600" data-testid="staff-attendance-summary">
        {days.summary.attendance_tracked
          ? `${days.summary.present_days} present · ${days.summary.absent_days} absent · ${days.summary.leave_days} on leave · ${days.summary.late_days} late`
          : "No clock records in this range, so nothing here counts as an absence."}
      </p>}
      {days?.items?.length ? <Table head={["Date", "Status", "Expected", "In", "Out", "Worked", "Late", "Undertime"]}>
        {days.items.map((day) => <tr key={day.date} className="border-b last:border-0">
          <td className="py-2 pr-4">{day.date}</td>
          <td className="py-2 pr-4 capitalize">{titleise(day.status)}{day.corrected ? " *" : ""}</td>
          <td className="py-2 pr-4 text-slate-600">{day.expected_start ? `${day.expected_start}–${day.expected_end}` : "—"}</td>
          <td className="py-2 pr-4">{day.first_in_at ? day.first_in_at.slice(11, 16) : "—"}</td>
          <td className="py-2 pr-4">{day.last_out_at ? day.last_out_at.slice(11, 16) : day.open_shift ? "open" : "—"}</td>
          <td className="py-2 pr-4">{day.worked_minutes ? hoursFrom(day.worked_minutes) : "—"}</td>
          <td className="py-2 pr-4">{day.late_minutes ? `${day.late_minutes}m` : "—"}</td>
          <td className="py-2 pr-4">{day.undertime_minutes ? `${day.undertime_minutes}m` : "—"}</td>
        </tr>)}
      </Table> : <Empty>No attendance in this range.</Empty>}
      <p className="mt-3 text-xs text-slate-500">* corrected. The original clock record is kept and stays readable.</p>
    </Card>

    {caps.attendance_correct && <Card title="Record a correction" testId="staff-correction-form">
      <form onSubmit={submitCorrection} className="grid gap-3 sm:grid-cols-5">
        <label className="text-sm">Date<input required type="date" className="mt-1 w-full rounded-lg border p-2"
          value={correction.work_date} onChange={(e) => setCorrection({ ...correction, work_date: e.target.value })} /></label>
        <label className="text-sm">Clock in<input type="time" className="mt-1 w-full rounded-lg border p-2"
          value={correction.clock_in_at} onChange={(e) => setCorrection({ ...correction, clock_in_at: e.target.value })} /></label>
        <label className="text-sm">Clock out<input type="time" className="mt-1 w-full rounded-lg border p-2"
          value={correction.clock_out_at} onChange={(e) => setCorrection({ ...correction, clock_out_at: e.target.value })} /></label>
        <label className="text-sm">Or status
          <select className="mt-1 w-full rounded-lg border p-2" value={correction.status_override}
                  onChange={(e) => setCorrection({ ...correction, status_override: e.target.value })}>
            <option value="">No change</option><option value="present">Present</option>
            <option value="absent">Absent</option><option value="on_leave">On leave</option>
            <option value="rest_day">Rest day</option><option value="closure">Closure</option>
          </select>
        </label>
        <label className="text-sm sm:col-span-4">Reason<input required minLength={3} className="mt-1 w-full rounded-lg border p-2"
          data-testid="staff-correction-reason" value={correction.reason}
          onChange={(e) => setCorrection({ ...correction, reason: e.target.value })} /></label>
        <button className="self-end rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">Record</button>
      </form>
      <p className="mt-3 text-xs text-slate-500">
        The employee is told that their record changed, and the original stays on file.
      </p>
    </Card>}

    {exceptions?.items?.length > 0 && <Card title="Unexplained attendance" testId="staff-exceptions">
      <Table head={["Person", "Absent", "Late", "Open shifts"]}>
        {exceptions.items.map((row) => <tr key={row.employee_id} className="border-b last:border-0">
          <td className="py-2 pr-4">{row.name}</td>
          <td className="py-2 pr-4">{row.absent_days}</td>
          <td className="py-2 pr-4">{row.late_days}</td>
          <td className="py-2 pr-4">{row.open_shifts}</td>
        </tr>)}
      </Table>
    </Card>}
  </div>;
}

// ---------------------------------------------------------------- Overtime --
function OvertimeTab({ employees, run }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ work_date: today(), scheduled_hours: 8, overtime_hours: 1, treatment: "history_only", reason: "" });
  const nameFor = (id) => employees.find((e) => e.id === id)?.name || String(id).slice(0, 8);

  const reload = useCallback(async () => {
    const [own, queue] = await Promise.all([
      api.get("/staff-management/overtime").catch(() => null),
      api.get("/staff-management/workspace/queues").catch(() => null),
    ]);
    const pending = queue?.data?.overtime || [];
    const mine = own?.data?.items || [];
    const seen = new Set(pending.map((r) => r.id));
    setRows([...pending.map((r) => ({ ...r, pending: true })), ...mine.filter((r) => !seen.has(r.id))]);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const submit = async (event) => {
    event.preventDefault();
    const ok = await run(() => api.post("/staff-management/overtime", form), "Overtime submitted.");
    if (ok) { setForm({ ...form, reason: "" }); await reload(); }
  };
  const decide = async (id, status) => {
    await run(() => api.post(`/staff-management/overtime/${id}/decision`, { status }), `Overtime ${status}.`);
    await reload();
  };

  return <div className="grid gap-5 lg:grid-cols-2" data-testid="staff-overtime">
    <Card title="Record overtime">
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">Date<input required type="date" className="mt-1 w-full rounded-lg border p-2"
          value={form.work_date} onChange={(e) => setForm({ ...form, work_date: e.target.value })} /></label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">Scheduled hours<input type="number" min="0" max="24" className="mt-1 w-full rounded-lg border p-2"
            value={form.scheduled_hours} onChange={(e) => setForm({ ...form, scheduled_hours: Number(e.target.value) })} /></label>
          <label className="block text-sm">Overtime hours<input type="number" min="0" max="24" className="mt-1 w-full rounded-lg border p-2"
            value={form.overtime_hours} onChange={(e) => setForm({ ...form, overtime_hours: Number(e.target.value) })} /></label>
        </div>
        <label className="block text-sm">Treatment
          <select className="mt-1 w-full rounded-lg border p-2" value={form.treatment}
                  onChange={(e) => setForm({ ...form, treatment: e.target.value })}>
            <option value="history_only">History only</option>
            <option value="paid">Paid overtime</option>
            <option value="toil">Convert to time off (TOIL)</option>
          </select>
        </label>
        <p className="text-xs text-slate-500">Time off in lieu credits a leave balance. The same hours are never also paid.</p>
        <label className="block text-sm">Reason<textarea className="mt-1 w-full rounded-lg border p-2" rows="2"
          value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
        <button className="rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">Submit overtime</button>
      </form>
    </Card>

    <Card title="Overtime records">
      {rows.length ? <div className="space-y-2">{rows.map((item) => <div key={item.id} className="rounded-lg border p-3 text-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <b>{item.work_date}</b>
          <span className="capitalize text-slate-600">{item.overtime_hours}h · {titleise(item.treatment)} · {item.status || "pending"}</span>
        </div>
        {item.employee_id && <p className="text-slate-600">{nameFor(item.employee_id)}</p>}
        {item.pending && <div className="mt-2">
          <button type="button" onClick={() => decide(item.id, "approved")}
                  data-testid={`staff-overtime-approve-${item.id}`}
                  className="mr-2 rounded-lg bg-emerald-800 px-3 py-1.5 text-xs font-semibold text-white">Approve</button>
          <button type="button" onClick={() => decide(item.id, "rejected")}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700">Reject</button>
        </div>}
      </div>)}</div> : <Empty>No overtime records yet.</Empty>}
    </Card>
  </div>;
}

// --------------------------------------------------------------- Documents --
function DocumentsTab({ employees, caps, run }) {
  const [employeeId, setEmployeeId] = useState(employees[0]?.id || "");
  const [documents, setDocuments] = useState([]);

  const reload = useCallback(async () => {
    if (!employeeId) return;
    const r = await api.get(`/staff-management/employees/${employeeId}/documents`).catch(() => null);
    setDocuments(r?.data?.items || []);
  }, [employeeId]);
  useEffect(() => { reload(); }, [reload]);

  return <div className="space-y-5" data-testid="staff-documents">
    <Card title="Employee">
      <select className="w-full rounded-lg border p-2" value={employeeId} data-testid="staff-documents-employee"
              aria-label="Documents employee" onChange={(e) => setEmployeeId(e.target.value)}>
        {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.id}</option>)}
      </select>
    </Card>
    <DocumentsCard title="Documents" employeeId={employeeId} documents={documents}
                   onChanged={reload} run={run} canVerify={caps.documents} />
  </div>;
}

function DocumentsCard({ title, employeeId, documents, onChanged, run, canVerify }) {
  const [form, setForm] = useState({ kind: "GOVERNMENT_ID", expiry_date: "", file: null });
  const [busy, setBusy] = useState(false);

  const upload = async (event) => {
    event.preventDefault();
    if (!form.file || !employeeId) return;
    setBusy(true);
    const body = {
      kind: form.kind, filename: form.file.name, content_type: form.file.type,
      data_base64: await fileToBase64(form.file),
      ...(form.expiry_date ? { expiry_date: form.expiry_date } : {}),
    };
    const ok = await run(() => api.post(`/staff-management/employees/${employeeId}/documents`, body),
                         "Document uploaded. It is recorded as uploaded, not as checked.");
    setBusy(false);
    if (ok) { setForm({ ...form, file: null, expiry_date: "" }); await onChanged(); }
  };

  const decide = async (id, state) => {
    // eslint-disable-next-line no-alert
    const reason = state === "REJECTED" ? window.prompt("Why is this document being rejected?") : "";
    if (state === "REJECTED" && !reason) return;
    await run(() => api.post(`/staff-management/documents/${id}/verification`, { state, reason }),
              `Document marked ${state.toLowerCase()}.`);
    await onChanged();
  };

  return <Card title={title} testId="staff-documents-card">
    <form onSubmit={upload} className="mb-5 grid gap-3 sm:grid-cols-4">
      <label className="text-sm">Type
        <select className="mt-1 w-full rounded-lg border p-2" value={form.kind} data-testid="staff-document-kind"
                onChange={(e) => setForm({ ...form, kind: e.target.value })}>
          {DOCUMENT_KINDS.map((kind) => <option key={kind} value={kind}>{titleise(kind)}</option>)}
        </select>
      </label>
      <label className="text-sm">Expires
        <input type="date" className="mt-1 w-full rounded-lg border p-2" value={form.expiry_date}
               onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
      </label>
      <label className="text-sm">File
        <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp,image/heic"
               className="mt-1 w-full rounded-lg border p-1.5" data-testid="staff-document-file"
               onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} />
      </label>
      <button disabled={busy || !form.file} className="self-end rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? "Uploading..." : "Upload"}
      </button>
    </form>

    {documents.length ? <Table head={["Type", "Version", "Expires", "State", "Decision"]}>
      {documents.map((doc) => <tr key={doc.id} className="border-b last:border-0">
        <td className="py-2 pr-4 capitalize">{titleise(doc.kind)}</td>
        <td className="py-2 pr-4 text-slate-600">v{doc.version}</td>
        <td className="py-2 pr-4">{doc.expiry_date || "—"}</td>
        <td className="py-2 pr-4 capitalize">{titleise(doc.verification?.state)}
          {doc.verification?.reason ? ` — ${doc.verification.reason}` : ""}</td>
        <td className="py-2 pr-4">
          {canVerify && doc.verification?.state !== "VERIFIED" && <button type="button"
            data-testid={`staff-document-verify-${doc.id}`} onClick={() => decide(doc.id, "VERIFIED")}
            className="mr-2 rounded-lg border border-emerald-700 px-2.5 py-1 text-xs font-medium text-emerald-800">Mark checked</button>}
          {canVerify && doc.verification?.state !== "REJECTED" && <button type="button"
            onClick={() => decide(doc.id, "REJECTED")}
            className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700">Reject</button>}
        </td>
      </tr>)}
    </Table> : <Empty>No documents on file.</Empty>}
    <p className="mt-3 text-xs text-slate-500">
      Uploading a document records it. It counts as checked only when somebody else marks it checked.
    </p>
  </Card>;
}

// ------------------------------------------------------------ Compensation --
function CompensationTab({ employees, caps, run }) {
  const [employeeId, setEmployeeId] = useState(employees[0]?.id || "");
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ effective_from: today(), basis: "monthly", amount: "", pay_frequency: "monthly" });

  const reload = useCallback(async () => {
    if (!employeeId) return;
    const r = await api.get(`/staff-management/employees/${employeeId}/compensation`).catch(() => null);
    setRows(r?.data?.items || []);
  }, [employeeId]);
  useEffect(() => { reload(); }, [reload]);

  const submit = async (event) => {
    event.preventDefault();
    const ok = await run(() => api.post("/staff-management/compensation", {
      employee_id: employeeId, effective_from: form.effective_from, basis: form.basis,
      amount_centavos: Math.round(Number(form.amount || 0) * 100),
      pay_frequency: form.pay_frequency,
    }), "Compensation recorded from the effective date.");
    if (ok) { setForm({ ...form, amount: "" }); await reload(); }
  };

  return <div className="space-y-5" data-testid="staff-compensation">
    <Card title="Compensation history" action={<select className="rounded-lg border p-1.5 text-sm" value={employeeId}
      data-testid="staff-compensation-employee" aria-label="Compensation employee"
      onChange={(e) => setEmployeeId(e.target.value)}>
      {employees.map((e) => <option key={e.id} value={e.id}>{e.name || e.id}</option>)}
    </select>}>
      {rows.length ? <Table head={["Effective from", "Basis", "Amount", "Frequency"]}>
        {rows.map((row) => <tr key={row.id} className="border-b last:border-0">
          <td className="py-2 pr-4">{row.effective_from}</td>
          <td className="py-2 pr-4 capitalize">{titleise(row.basis)}</td>
          <td className="py-2 pr-4 font-medium">{peso(row.amount_centavos)}</td>
          <td className="py-2 pr-4 capitalize">{titleise(row.pay_frequency)}</td>
        </tr>)}
      </Table> : <Empty>No compensation recorded for this person.</Empty>}
      <p className="mt-3 text-xs text-slate-500">
        A new rate is a new record from its effective date. Earlier periods keep the rate they were paid at.
      </p>
    </Card>

    {caps.compensation_manage && <Card title="Set a rate">
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-5">
        <label className="text-sm">Effective from<input required type="date" className="mt-1 w-full rounded-lg border p-2"
          value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} /></label>
        <label className="text-sm">Basis
          <select className="mt-1 w-full rounded-lg border p-2" value={form.basis}
                  onChange={(e) => setForm({ ...form, basis: e.target.value })}>
            {["monthly", "daily", "hourly", "per_trip", "per_booking", "commission", "mixed"].map((b) =>
              <option key={b} value={b}>{titleise(b)}</option>)}
          </select>
        </label>
        <label className="text-sm">Amount (₱)<input required type="number" min="0" step="0.01" className="mt-1 w-full rounded-lg border p-2"
          data-testid="staff-compensation-amount" value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })} /></label>
        <label className="text-sm">Frequency
          <select className="mt-1 w-full rounded-lg border p-2" value={form.pay_frequency}
                  onChange={(e) => setForm({ ...form, pay_frequency: e.target.value })}>
            {["monthly", "semi_monthly", "biweekly", "weekly", "custom"].map((f) =>
              <option key={f} value={f}>{titleise(f)}</option>)}
          </select>
        </label>
        <button className="self-end rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">Save rate</button>
      </form>
    </Card>}
  </div>;
}

// ----------------------------------------------------------------- Payroll --
function PayrollTab({ caps, run }) {
  const [periods, setPeriods] = useState([]);
  const [rules, setRules] = useState(null);
  const [form, setForm] = useState({ name: "", start_date: today(), end_date: today(), pay_frequency: "monthly" });
  const [items, setItems] = useState({ periodId: null, rows: [] });

  const reload = useCallback(async () => {
    const [p, r] = await Promise.all([
      api.get("/staff-management/payroll").catch(() => null),
      api.get("/staff-management/payroll/rules").catch(() => null),
    ]);
    setPeriods(p?.data?.periods || []);
    setRules(r?.data || null);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const create = async (event) => {
    event.preventDefault();
    const ok = await run(() => api.post("/staff-management/payroll/periods", form), "Payroll period created.");
    if (ok) { setForm({ ...form, name: "" }); await reload(); }
  };
  const calculate = async (id) => {
    const result = await run(() => api.post(`/staff-management/payroll/periods/${id}/calculate`),
                             "Payroll calculated and held for review.");
    if (result) setItems({ periodId: id, rows: result.data.items || [] });
    await reload();
  };
  const setStatus = async (id, status) => {
    await run(() => api.post(`/staff-management/payroll/periods/${id}/status`, { status }), `Payroll ${status}.`);
    await reload();
  };
  const issue = async (id) => {
    await run(() => api.post(`/staff-management/payroll/periods/${id}/payslips`), "Payslips issued.");
    await reload();
  };

  return <div className="space-y-5" data-testid="staff-payroll">
    {rules && rules.missing?.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="staff-payroll-rules-missing">
      <b>No contribution rules are in force for: {rules.missing.map((k) => rules.kinds[k]).join(", ")}.</b>
      <p className="mt-1">Nothing is deducted for them, and every payroll item says so. ConfirmPH does not
      ship these figures: enter the current table with its source and effective date.</p>
    </div>}

    <Card title="Create payroll period">
      <form onSubmit={create} className="grid gap-3 sm:grid-cols-4">
        <input required aria-label="Payroll name" placeholder="Payroll name" className="rounded-lg border p-2"
               data-testid="staff-payroll-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input required aria-label="Start date" type="date" className="rounded-lg border p-2"
               value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        <input required aria-label="End date" type="date" className="rounded-lg border p-2"
               value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        <button className="rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white">Create period</button>
      </form>
    </Card>

    <Card title="Payroll periods">
      {periods.length ? <div className="space-y-2">{periods.map((period) => <div key={period.id}
        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm">
        <div>
          <b>{period.name}</b>
          <p className="text-slate-600">{period.start_date} to {period.end_date} · <span className="capitalize">{period.status}</span> · {period.item_count || 0} item(s)</p>
          {period.books_status && <p className="text-xs text-emerald-800">Posted to Books ({period.books_status})</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {["draft", "review"].includes(period.status) && <button type="button" onClick={() => calculate(period.id)}
            data-testid={`staff-payroll-calculate-${period.id}`}
            className="rounded-lg border border-emerald-700 px-3 py-1.5 font-medium text-emerald-800">Calculate</button>}
          {period.status === "review" && caps.payroll_approve && <button type="button" onClick={() => setStatus(period.id, "approved")}
            data-testid={`staff-payroll-approve-${period.id}`}
            className="rounded-lg bg-emerald-800 px-3 py-1.5 font-semibold text-white">Approve</button>}
          {["approved", "paid", "closed"].includes(period.status) && caps.payroll_approve && <button type="button"
            onClick={() => issue(period.id)} data-testid={`staff-payroll-issue-${period.id}`}
            className="rounded-lg border border-emerald-700 px-3 py-1.5 font-medium text-emerald-800">Issue payslips</button>}
        </div>
      </div>)}</div> : <Empty>No payroll periods yet.</Empty>}
    </Card>

    {items.rows.length > 0 && <Card title="Latest calculation" testId="staff-payroll-items">
      <Table head={["Person", "Gross", "Statutory", "Deductions", "Net", "Notes"]}>
        {items.rows.map((row) => <tr key={row.id} className="border-b last:border-0 align-top">
          <td className="py-2 pr-4">{row.employee_name}</td>
          <td className="py-2 pr-4">{peso(row.gross_pay_centavos)}</td>
          <td className="py-2 pr-4">{peso(row.statutory_deductions_centavos)}</td>
          <td className="py-2 pr-4">{peso(row.deductions_centavos)}</td>
          <td className="py-2 pr-4 font-medium">{peso(row.net_pay_centavos)}</td>
          <td className="py-2 pr-4 text-xs text-amber-800">{(row.warnings || []).join(" ")}</td>
        </tr>)}
      </Table>
    </Card>}

    {rules && <Card title="Payroll rules" testId="staff-payroll-rules">
      {rules.items?.length ? <Table head={["Rule", "Version", "Effective", "Status", "Source"]}>
        {rules.items.map((rule) => <tr key={rule.id} className="border-b last:border-0">
          <td className="py-2 pr-4">{rules.kinds[rule.kind] || rule.kind}</td>
          <td className="py-2 pr-4">v{rule.version}</td>
          <td className="py-2 pr-4">{rule.effective_from}{rule.effective_to ? ` → ${rule.effective_to}` : ""}</td>
          <td className="py-2 pr-4 capitalize">{rule.status}</td>
          <td className="py-2 pr-4 text-slate-600">{rule.source?.name} · {rule.source?.reference}</td>
        </tr>)}
      </Table> : <Empty>No payroll rules have been entered.</Empty>}
    </Card>}
  </div>;
}

// ----------------------------------------------------------------- Reports --
function ReportsTab({ caps }) {
  const [range, setRange] = useState({ start: daysAgo(30), end: today() });
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await api.get(`/staff-management/reports/staff?start=${range.start}&end=${range.end}`);
      setReport(r.data);
    } catch (err) { setError(formatApiError(err)); setReport(null); }
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const download = async () => {
    const r = await api.get(`/staff-management/reports/staff?start=${range.start}&end=${range.end}&fmt=csv`,
                            { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url; link.download = `staff-report-${range.start}-to-${range.end}.csv`;
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(url);
  };

  return <Card title="Staff report" testId="staff-report" action={<div className="flex flex-wrap gap-2">
    <input type="date" aria-label="Report start" className="rounded-lg border p-1.5 text-sm"
           value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
    <input type="date" aria-label="Report end" className="rounded-lg border p-1.5 text-sm"
           value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
    <button type="button" onClick={download} data-testid="staff-report-csv"
            className="rounded-lg border border-emerald-700 px-3 py-1.5 text-sm font-medium text-emerald-800">Download CSV</button>
  </div>}>
    {error && <div role="alert" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{error}</div>}
    {report?.items?.length ? <Table head={[
      "Person", "Scheduled", "Present", "Absent", "Leave", "Late", "Worked hours", "Paid OT",
      ...(report.includes_pay ? ["Gross", "Net"] : [])]}>
      {report.items.map((row) => <tr key={row.employee_id} className="border-b last:border-0">
        <td className="py-2 pr-4">{row.name}</td>
        <td className="py-2 pr-4">{row.scheduled_days}</td>
        <td className="py-2 pr-4">{row.present_days}</td>
        <td className="py-2 pr-4">{row.absent_days}</td>
        <td className="py-2 pr-4">{row.leave_days}</td>
        <td className="py-2 pr-4">{row.late_days}</td>
        <td className="py-2 pr-4">{row.worked_hours}</td>
        <td className="py-2 pr-4">{row.paid_overtime_hours}</td>
        {report.includes_pay && <td className="py-2 pr-4">{peso(row.gross_pay_centavos)}</td>}
        {report.includes_pay && <td className="py-2 pr-4 font-medium">{peso(row.net_pay_centavos)}</td>}
      </tr>)}
    </Table> : <Empty>Nothing to report for this range.</Empty>}
    {!caps.compensation && <p className="mt-3 text-xs text-slate-500" data-testid="staff-report-no-pay">
      Pay figures are not included: that needs the compensation permission.
    </p>}
  </Card>;
}
